import { useEffect, useState } from "react";
import { openOtherUserPanel } from "../../components/shared/User/OtherUserPanel/functions";
import {
    AccountType,
    UnapprovedUser,
    User,
} from "../../components/shared/User/types";
// import { mergeLocalStorageAndSavedUserData } from "../../components/zApps/Layout/Login/MyPage/progressManager";
import { Batch } from "../../types/Batch";
import { Course } from "../../types/Course";
import { StudentLessonRelation } from "../../types/LessonStatus";
import { FetchResult } from "../../types/fetch";
import { changeAppState, getAppState, useAppState } from "../appState";

/**
 * Procedure to add a state that needs to be fetched initially:
 *  - Add the state to AppState
 *  - Add data fetch to fetchInitialDataForUser (Called when a user visit with a valid cookie and when a user sign-in)
 *  - Clean it up in cleanUpForSignOut (Called when the user signs out)
 *  - If RightPanel needs to be opened initially, write it in openInitialPanelForUser or openInitialPanelIncludingGuestUser
 */

export function useStateLifecycle() {
    const [isInitialFetchFinished, setInitialFetchFinished] = useState(false);
    useEffect(() => {
        (async () => {
            const panelOpened = openInitialPanelIncludingGuestUser();

            await fetchUser();
            const { user } = getAppState();
            if (user) {
                await fetchDataForUser(user.userId, true);
                if (!panelOpened) {
                    // await openInitialPanelForUser(user);
                }
                // await openInitialDialogForUser(user.userId);
            }

            setInitialFetchFinished(true);
        })();
    }, []);

    const [user] = useAppState("user");
    const userId = user?.userId;
    useEffect(() => {
        if (isInitialFetchFinished) {
            // When sign in/out

            if (!userId) {
                // sign out
                cleanUpForSignOut();
                return;
            }

            // sign in
            (async () => {
                await fetchDataForUser(userId, true);
                // await openInitialPanelForUser(user);
                // await openInitialDialogForUser(userId);
            })();
        }
    }, [userId]);

    useEffect(() => {
        if (userId == null) {
            return;
        }

        const intervalId = setInterval(() => {
            fetchDataForUser(userId);
        }, 60 * 1000);

        return () => {
            clearInterval(intervalId);
        };
    }, [userId]);
}

async function fetchDataForUser(userId: number, initial?: boolean) {
    const response = await fetch(
        `api/User/GetDataForUser?userId=${userId}&initial=${!!initial}`,
        {
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            credentials: "include",
        }
    );
    const userDataResult: FetchResult<UserDataResult> = await response.json();

    if ("error" in userDataResult) {
        return;
    }

    const {
        initialDataForUser,
        dataForUser: {
            maintenanceRemainingMinutes,
            unapprovedUsers,
            batches,
            user,
            finishedLessonStatus,
        },
    } = userDataResult;

    // When initializing and once a minute
    // (Data that is necessary to be updated once a minute)
    changeAppState("maintenanceRemainingMinutes", maintenanceRemainingMinutes);
    changeAppState("unapprovedUsers", unapprovedUsers);
    changeAppState("batches", batches);
    changeAppState("finishedLessonStatus", finishedLessonStatus);

    const currentUser = getAppState().user;
    if (
        currentUser &&
        (user.accountType !== currentUser.accountType ||
            JSON.stringify(user.courseIds) !==
                JSON.stringify(currentUser.courseIds))
    ) {
        // アカウントが承認されたとき、削除されたとき、
        // コースが変更されたとき
        changeAppState("user", user);
    }

    if (
        user.accountType === AccountType.Unapproved &&
        location.pathname !== "/"
    ) {
        // 未承認ユーザーは、トップページへ遷移
        location.href = "/";
    }

    if (initialDataForUser) {
        // Only when initializing the user
        changeAppState("courses", initialDataForUser.courses);
    }
}
type InitialDataForUser = {
    courses: Course[];
};
type DataForUser = {
    maintenanceRemainingMinutes: number;
    unapprovedUsers: UnapprovedUser[];
    batches: Batch[];
    user: User;
    finishedLessonStatus: StudentLessonRelation[];
};
type UserDataResult = {
    initialDataForUser?: InitialDataForUser;
    dataForUser: DataForUser;
};

// Sign out
function cleanUpForSignOut() {
    // changeAppState("relatedUsers", []);
    // changeAppState("unreadMessages", []);
    // changeAppState("questState", initialQuestState);
    // changeAppState("xpBoostRemainingSeconds", 0);
}

/**
 * Open a panel for everyone even if they didn't login
 * @returns
 *  - true: A panel was opened
 *  - false: No panel was opened
 */
function openInitialPanelIncludingGuestUser(): boolean {
    const { hash } = window.location;
    /**
     * OtherUser panel
     */
    if (/^#u\d+$/.test(hash)) {
        openOtherUserPanel(Number(hash.replace("#u", "")));
        return true;
    }

    return false;
}

async function fetchUser() {
    const response = await fetch("api/Auth/GetUser", {
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        credentials: "include",
    });
    const user: FetchResult<User> = await response.json();

    if ("error" in user) {
        changeAppState("user", undefined);
    } else {
        changeAppState("user", user);
    }
    changeAppState("isUserFetchDone", true);
}
